import { useState } from 'react';
import { Plus, Key, RefreshCw, Trash2, AlertTriangle, Clock, Shield, Sparkles, FolderOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTokens, formatTokenExpiry, formatLastUsed, TOKEN_SCOPES } from '../hooks/useTokens';
import { useProjects } from '../hooks/useProjects';
import { useToast } from '../components/ui/toast';
import { CreateTokenModal } from '../components/tokens/CreateTokenModal';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function TokensPage() {
  const toast = useToast();
  const { projects, isLoadingProjects } = useProjects();

  const {
    tokens,
    isLoading,
    refetch,
    createToken,
    revokeToken,
    isRevoking,
  } = useTokens();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<string | null>(null);
  const [revokeConfirmName, setRevokeConfirmName] = useState('');

  const tokenBeingRevoked = tokens.find((t) => t.id === tokenToRevoke);

  const handleRevokeConfirm = async () => {
    if (!tokenToRevoke) return;

    try {
      await revokeToken(tokenToRevoke);
      setTokenToRevoke(null);
      setRevokeConfirmName('');
      toast.success('Token revoked successfully');
    } catch (error) {
      console.error('Failed to revoke token:', error);
      toast.error('Failed to revoke token');
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="max-w-6xl mx-auto"
    >
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
      >
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Service Tokens</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create tokens for CI/CD pipelines and automated access
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/30">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => refetch()}
              title="Refresh"
              className="hover:bg-muted/50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            size="sm"
            className="gap-1.5 shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow"
          >
            <Plus className="h-4 w-4" />
            Create Token
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="glass border-primary/20 mb-6">
          <CardContent className="p-5">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5 p-2 rounded-lg bg-primary/10">
                <Shield className="h-4 w-4 text-primary" />
              </div>
              <div className="text-sm">
                <p className="font-medium text-foreground">About Service Tokens</p>
                <p className="mt-1 text-muted-foreground">
                  Service tokens provide programmatic access to secrets for CI/CD pipelines,
                  deployment scripts, and the CLI. Each token can be scoped to one or more
                  projects and has specific permissions (read/write).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="p-0">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-5 border-b border-border/50 last:border-b-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-muted/50 animate-shimmer" />
                          <div className="space-y-2">
                            <div className="h-4 w-32 bg-muted/50 rounded animate-shimmer" />
                            <div className="h-3 w-20 bg-muted/30 rounded animate-shimmer" />
                          </div>
                        </div>
                        <div className="mt-4 flex gap-4">
                          <div className="h-3 w-24 bg-muted/30 rounded animate-shimmer" />
                          <div className="h-3 w-32 bg-muted/30 rounded animate-shimmer" />
                        </div>
                      </div>
                      <div className="h-8 w-8 rounded-lg bg-muted/30 animate-shimmer" />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ) : tokens.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="glass overflow-hidden">
              <CardContent className="py-16 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-muted/30 mb-4"
                >
                  <Key className="h-8 w-8 text-muted-foreground/50" />
                </motion.div>
                <motion.h3 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-lg font-medium text-foreground"
                >
                  No service tokens
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto"
                >
                  Create a service token to enable automated access to your secrets.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="mt-6 gap-2 shadow-lg shadow-primary/20"
                  >
                    <Sparkles className="h-4 w-4" />
                    Create Your First Token
                  </Button>
                </motion.div>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            key="tokens"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Card className="glass overflow-hidden">
              <div className="divide-y divide-border/50">
                {tokens.map((token, index) => {
                  const isExpired =
                    token.expires_at && new Date(token.expires_at) < new Date();

                  return (
                    <motion.div
                      key={token.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.3, 
                        delay: index * 0.05,
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      whileHover={{ backgroundColor: 'hsl(var(--muted) / 0.1)' }}
                      className={`p-5 transition-colors ${isExpired ? 'bg-destructive/5' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <motion.div 
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className={`p-2.5 rounded-lg ${isExpired ? 'bg-destructive/10' : 'bg-primary/10'}`}
                            >
                              <Key className={`h-4 w-4 ${isExpired ? 'text-destructive' : 'text-primary'}`} />
                            </motion.div>
                            <div>
                              <h3 className="text-base font-medium text-foreground">
                                {token.name}
                              </h3>
                              {isExpired && (
                                <Badge variant="destructive" className="mt-1">Expired</Badge>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <FolderOpen className="h-3.5 w-3.5" />
                              <span>
                                {token.projects.length === 0 ? (
                                  <span className="italic">No projects</span>
                                ) : token.projects.length <= 3 ? (
                                  token.projects.map(p => p.name).join(', ')
                                ) : (
                                  <>
                                    {token.projects.slice(0, 2).map(p => p.name).join(', ')}
                                    <span className="ml-1 text-xs bg-muted/50 px-1.5 py-0.5 rounded-full">
                                      +{token.projects.length - 2} more
                                    </span>
                                  </>
                                )}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <span className="font-medium text-foreground/80">Permissions:</span>
                              <span>
                                {token.scopes
                                  .map(
                                    (s) =>
                                      TOKEN_SCOPES.find((ts) => ts.value === s)?.label || s
                                  )
                                  .join(', ')}
                              </span>
                            </div>
                          </div>

                          <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5" />
                              <span>{formatTokenExpiry(token.expires_at)}</span>
                            </div>
                            <div>
                              Last used: {formatLastUsed(token.last_used_at)}
                            </div>
                          </div>
                        </div>

                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => setTokenToRevoke(token.id)}
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            title="Revoke token"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </motion.div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 300, damping: 25 }}
        className="fixed bottom-8 right-8"
      >
        <Button
          size="lg"
          className="gap-2 shadow-2xl shadow-primary/30 hover:shadow-primary/40 transition-all hover:scale-105 active:scale-95"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Sparkles className="h-4 w-4" />
          Create Token
        </Button>
      </motion.div>

      {!isLoadingProjects && projects.length > 0 && (
        <CreateTokenModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={createToken}
          projects={projects}
        />
      )}

      <AnimatePresence>
        {tokenToRevoke && tokenBeingRevoked && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => {
                setTokenToRevoke(null);
                setRevokeConfirmName('');
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              <Card className="glass relative w-full max-w-md mx-4 border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}
                      className="p-2.5 bg-destructive/10 rounded-xl"
                    >
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                    </motion.div>
                    <h2 className="text-lg font-semibold text-foreground">Revoke Token</h2>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">
                    Are you sure you want to revoke the token{' '}
                    <strong className="text-foreground">{tokenBeingRevoked.name}</strong>? Any services using this token
                    will immediately lose access.
                  </p>

                  <div className="mb-4">
                    <Label htmlFor="confirm-name" className="text-sm font-medium">
                      Type the token name to confirm:
                    </Label>
                    <Input
                      id="confirm-name"
                      type="text"
                      value={revokeConfirmName}
                      onChange={(e) => setRevokeConfirmName(e.target.value)}
                      placeholder={tokenBeingRevoked.name}
                      className="mt-2 bg-muted/30 border-transparent focus:border-destructive/50 focus:bg-muted/50"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setTokenToRevoke(null);
                        setRevokeConfirmName('');
                      }}
                      disabled={isRevoking}
                      className="hover:bg-muted/50"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleRevokeConfirm}
                      disabled={
                        isRevoking || revokeConfirmName !== tokenBeingRevoked.name
                      }
                      className="shadow-lg shadow-destructive/20"
                    >
                      {isRevoking ? 'Revoking...' : 'Revoke Token'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
